<template>
  <div v-if="show" class="nim-overlay" @click.self="close">
    <div class="nim-modal">
      <!-- Header -->
      <div class="nim-header" :class="headerClass">
        <div class="nim-header-content">
          <div class="nim-icon-wrap">
            <i class="material-icons">{{ info.icon }}</i>
          </div>
          <div>
            <h5 class="nim-title">{{ info.title }}</h5>
            <span class="nim-type-badge">
              <i class="material-icons" style="font-size:14px;">{{ info.typeBadgeIcon }}</i>
              {{ info.typeBadgeLabel }}
            </span>
          </div>
        </div>
        <button class="nim-close" @click="close">
          <i class="material-icons">close</i>
        </button>
      </div>

      <!-- Body -->
      <div class="nim-body">
        <!-- Channel indicator -->
        <div class="nim-channel-box" :class="channelClass">
          <div class="nim-channel-icon-lg">
            <i class="material-icons">{{ info.channelIcon }}</i>
          </div>
          <div>
            <strong>{{ info.channelLabel }}</strong>
            <p class="mb-0">{{ info.channelDescription }}</p>
          </div>
        </div>

        <!-- What it does -->
        <div class="nim-section">
          <h6 class="nim-section-title"><i class="material-icons">description</i> What This Does</h6>
          <p class="nim-text">{{ info.explanation }}</p>
        </div>

        <!-- How it works — visual steps -->
        <div class="nim-section">
          <h6 class="nim-section-title"><i class="material-icons">account_tree</i> How It Works</h6>
          <div class="nim-steps">
            <div v-for="(step, idx) in info.steps" :key="idx" class="nim-step">
              <div class="nim-step-number" :class="headerClass">{{ idx + 1 }}</div>
              <div class="nim-step-icon-wrap" :class="stepIconClass">
                <i class="material-icons">{{ step.icon }}</i>
              </div>
              <div class="nim-step-text">{{ step.text }}</div>
              <div v-if="idx < info.steps.length - 1" class="nim-step-arrow">
                <i class="material-icons">arrow_downward</i>
              </div>
            </div>
          </div>
        </div>

        <!-- Settings (if any) -->
        <div v-if="info.settings && info.settings.length" class="nim-section">
          <h6 class="nim-section-title"><i class="material-icons">settings</i> Configuration</h6>
          <div class="nim-settings-grid">
            <div v-for="(setting, si) in info.settings" :key="si" class="nim-setting-card">
              <div class="nim-setting-icon">
                <i class="material-icons">{{ setting.icon }}</i>
              </div>
              <div>
                <div class="nim-setting-label">{{ setting.label }}</div>
                <div class="nim-setting-desc">{{ setting.description }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- What you'll receive -->
        <div class="nim-section">
          <h6 class="nim-section-title"><i class="material-icons">emoji_objects</i> What You'll Receive</h6>
          <div class="nim-outcome" :class="channelClass">
            <i class="material-icons nim-outcome-icon">{{ info.channelIcon }}</i>
            <span>{{ info.outcome }}</span>
          </div>
        </div>

        <!-- Example -->
        <div class="nim-section">
          <h6 class="nim-section-title"><i class="material-icons">lightbulb</i> Example</h6>
          <div class="nim-example">
            <i class="material-icons nim-example-icon">format_quote</i>
            <p class="mb-0">{{ info.example }}</p>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="nim-footer">
        <button class="nim-footer-btn" @click="close">Got it</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  ruleId: { type: [Number, String], default: null },
});

const emit = defineEmits(['update:show', 'close']);
function close() {
  emit('update:show', false);
  emit('close');
}

/* ── Notification rule data ── */
const NOTIFICATION_RULES = {
  1: {
    title: 'Upcoming Assignments Email',
    icon: 'mark_email_read',
    typeBadgeIcon: 'schedule_send',
    typeBadgeLabel: 'SCHEDULED EMAIL',
    channelIcon: 'email',
    channelLabel: 'Email Notification',
    channelDescription: 'A periodic digest email is sent automatically based on the interval you configure. No manual action is needed once set up.',
    explanation:
      'This rule sends one consolidated email listing all of your assignments during a future time window. You control how often the report is sent and how many days into the future each report includes.',
    steps: [
      { icon: 'date_range', text: 'Set how often the email is sent (e.g. every 7 days)' },
      { icon: 'visibility', text: 'Set how many days ahead to show assignments (e.g. next 14 days)' },
      { icon: 'auto_awesome', text: 'System automatically gathers all of your assignments within that window' },
      { icon: 'outgoing_mail', text: 'A neatly formatted summary email is sent to you on schedule' },
    ],
    settings: [
      {
        icon: 'timer',
        label: 'Send Interval (7–17 days)',
        description: 'How often the report email is sent. For example, every 7 days means you get a weekly digest.',
      },
      {
        icon: 'date_range',
        label: 'Display Window (14–31 days)',
        description: 'How far into the future the report looks. A 14-day window shows your assignments for the next two weeks.',
      },
    ],
    outcome: 'You will receive a clean, easy-to-read email listing all your scheduled assignments for the configured time window. Each assignment shows the date, time, activity, and workgroup.',
    example: 'With interval set to 7 days and display window set to 14 days: Every Monday you receive an email showing all your assignments for the next two weeks — "Mar 10 @ 9 AM — Greeting Team, Mar 17 @ 11 AM — Usher Team".',
  },
  2: {
    title: 'Team Assignment Email',
    icon: 'groups',
    typeBadgeIcon: 'campaign',
    typeBadgeLabel: 'EVENT ALERT',
    channelIcon: 'email',
    channelLabel: 'Email Notification',
    channelDescription: 'An automatic email alert sent a few days before each event where you are assigned to a workgroup with other people.',
    explanation:
      'When you are assigned to a workgroup with other people, this rule controls whether you receive an email showing everyone serving with you for that event. The email is sent a few days before the event date.',
    steps: [
      { icon: 'group', text: 'System detects workgroups where multiple people are assigned together' },
      { icon: 'fact_check', text: 'Checks whether you have this notification turned on ("Send")' },
      { icon: 'schedule', text: 'A few days before the event, the system prepares the team list' },
      { icon: 'outgoing_mail', text: 'You receive an email showing who else is on your team for that event' },
    ],
    settings: [],
    outcome: 'You will receive an email before each event showing your team — the names of all people assigned to the same workgroup(s) as you. This makes it easy to coordinate, carpool, or prepare together.',
    example: 'You are assigned to the Audio/Visual team for Sunday March 10. On Thursday March 7, you receive an email: "Your team for March 10 A/V: You, Sarah Johnson, and Mike Chen." Now everyone knows who\'s serving together.',
  },
  3: {
    title: 'Assignment Text Reminder',
    icon: 'sms',
    typeBadgeIcon: 'phonelink_ring',
    typeBadgeLabel: 'TEXT MESSAGE',
    channelIcon: 'textsms',
    channelLabel: 'SMS / Text Message',
    channelDescription: 'A short text message reminder sent to your phone a few days before each event you are assigned to.',
    explanation:
      'This rule controls whether you receive a text message reminder before each event where you have an assignment. The message is sent automatically a few days before the event.',
    steps: [
      { icon: 'event', text: 'System identifies your upcoming assignments' },
      { icon: 'fact_check', text: 'Checks whether you have SMS reminders turned on ("Send")' },
      { icon: 'schedule', text: 'A few days before the event date, the reminder is queued' },
      { icon: 'phone_android', text: 'You receive a brief text message with the event details' },
    ],
    settings: [],
    outcome: 'You will receive a short text message on your phone reminding you of your upcoming assignment. The message includes the date, time, and what you are scheduled for.',
    example: 'You are scheduled for Greeting Team on Sunday March 10 at 9 AM. On Friday March 8, you receive a text: "Reminder: You are scheduled for Greeting Team on Sun, Mar 10 at 9:00 AM." Simple and effective.',
  },
  4: {
    title: 'Open Sign-Up Opportunities Email',
    icon: 'assignment_ind',
    typeBadgeIcon: 'schedule_send',
    typeBadgeLabel: 'SCHEDULED EMAIL',
    channelIcon: 'email',
    channelLabel: 'Email Notification',
    channelDescription: 'A periodic email showing open (unfilled) sign-up assignments that you can volunteer for.',
    explanation:
      'This rule sends one consolidated email listing open manual sign-up opportunities that you are eligible to join. You control how often the report is sent and how many days into the future each report searches for opportunities.',
    steps: [
      { icon: 'date_range', text: 'Set how often the email is sent (e.g. every 7 days)' },
      { icon: 'visibility', text: 'Set how many days ahead to search for eligible opportunities (e.g. next 30 days)' },
      { icon: 'search', text: 'System scans for open manual sign-up positions that match your eligibility' },
      { icon: 'outgoing_mail', text: 'You receive an email listing the opportunities you can sign up for' },
    ],
    settings: [
      {
        icon: 'timer',
        label: 'Send Interval (7–28 days)',
        description: 'How often you receive the open sign-up report. A 7-day interval means a weekly update on available slots.',
      },
      {
        icon: 'date_range',
        label: 'Display Window (14–45 days)',
        description: 'How far ahead to look for open slots. A 30-day window shows all open assignments for the next month.',
      },
    ],
    outcome: 'You will receive a report listing eligible open manual sign-up opportunities within the configured window. Each entry shows the date, time, activity, and workgroup that still needs volunteers.',
    example: 'With interval set to 7 days and display window set to 30 days: Every Monday you receive an email like "Open Sign-Up Slots: Mar 15 @ 9 AM — Usher (2 spots), Mar 22 @ 11 AM — Greeting (1 spot)". You can then sign up for any that interest you.',
  },
};

const FALLBACK = {
  title: 'Notification Setting',
  icon: 'notifications',
  typeBadgeIcon: 'info',
  typeBadgeLabel: 'NOTIFICATION',
  channelIcon: 'notifications_active',
  channelLabel: 'Notification',
  channelDescription: 'This controls a notification preference for your account.',
  explanation: 'Configure this setting to control how and when you receive notifications.',
  steps: [
    { icon: 'settings', text: 'Configure the notification parameters' },
    { icon: 'save', text: 'Save your preference' },
    { icon: 'notifications_active', text: 'Notifications will be sent based on your settings' },
  ],
  settings: [],
  outcome: 'Notifications will be delivered based on your configured preferences.',
  example: 'Adjust the settings and save to start receiving notifications.',
};

const info = computed(() => {
  return NOTIFICATION_RULES[props.ruleId] || FALLBACK;
});

/* ── Styling helpers ── */
const isSms = computed(() => info.value.channelIcon === 'textsms');

const headerClass = computed(() => {
  if (isSms.value) return 'nim-header-sms';
  if (props.ruleId === 4) return 'nim-header-signup';
  return 'nim-header-email';
});

const channelClass = computed(() => {
  if (isSms.value) return 'nim-channel-sms';
  if (props.ruleId === 4) return 'nim-channel-signup';
  return 'nim-channel-email';
});

const stepIconClass = computed(() => {
  if (isSms.value) return 'nim-stepicon-sms';
  if (props.ruleId === 4) return 'nim-stepicon-signup';
  return 'nim-stepicon-email';
});
</script>

<style scoped>
.nim-overlay {
  position: fixed;
  inset: 0;
  z-index: 2060;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.nim-modal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: nimSlideUp 0.25s ease;
}

@keyframes nimSlideUp {
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Header ── */
.nim-header {
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  color: #fff;
}
.nim-header-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.nim-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.nim-icon-wrap i { font-size: 24px; }
.nim-title {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
  color: #fff;
}
.nim-close {
  background: rgba(255,255,255,0.2);
  border: none;
  border-radius: 8px;
  color: #fff;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
}
.nim-close:hover { background: rgba(255,255,255,0.35); }

/* Header color variants */
.nim-header-email  { background: linear-gradient(135deg, #1976d2, #1565c0); }
.nim-header-sms    { background: linear-gradient(135deg, #00897b, #00695c); }
.nim-header-signup { background: linear-gradient(135deg, #7b1fa2, #6a1b9a); }

/* Type badge */
.nim-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-top: 4px;
  background: rgba(255,255,255,0.25);
  color: #fff;
}

/* ── Body ── */
.nim-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem 1.5rem;
}

/* Channel indicator box */
.nim-channel-box {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  margin-bottom: 1.25rem;
  font-size: 0.82rem;
  line-height: 1.5;
}
.nim-channel-icon-lg {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
}
.nim-channel-icon-lg i { font-size: 20px; }

.nim-channel-email {
  background: #e3f2fd;
  border-left: 4px solid #1976d2;
}
.nim-channel-email .nim-channel-icon-lg { background: #1976d2; }

.nim-channel-sms {
  background: #e0f2f1;
  border-left: 4px solid #00897b;
}
.nim-channel-sms .nim-channel-icon-lg { background: #00897b; }

.nim-channel-signup {
  background: #f3e5f5;
  border-left: 4px solid #7b1fa2;
}
.nim-channel-signup .nim-channel-icon-lg { background: #7b1fa2; }

/* Sections */
.nim-section {
  margin-bottom: 1.25rem;
}
.nim-section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.88rem;
  font-weight: 700;
  color: #344767;
  margin-bottom: 0.5rem;
}
.nim-section-title i {
  font-size: 18px;
  color: #1976d2;
}
.nim-text {
  font-size: 0.82rem;
  color: #555;
  line-height: 1.6;
  margin: 0;
}

/* Steps */
.nim-steps {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.nim-step {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  position: relative;
  padding-bottom: 0.25rem;
}
.nim-step-number {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
  margin-top: 2px;
}
.nim-step-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.nim-step-icon-wrap i { font-size: 18px; }

.nim-stepicon-email { background: #e3f2fd; }
.nim-stepicon-email i { color: #1565c0; }
.nim-stepicon-sms   { background: #e0f2f1; }
.nim-stepicon-sms i   { color: #00695c; }
.nim-stepicon-signup { background: #f3e5f5; }
.nim-stepicon-signup i { color: #6a1b9a; }

.nim-step-text {
  font-size: 0.8rem;
  color: #444;
  line-height: 1.5;
  padding-top: 5px;
}
.nim-step-arrow {
  position: absolute;
  left: 10px;
  bottom: -6px;
  color: #bbb;
}
.nim-step-arrow i { font-size: 16px; }

/* Settings grid */
.nim-settings-grid {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.nim-setting-card {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  background: #f8f9fb;
  border: 1px solid #e8eaf0;
}
.nim-setting-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #e8eaf6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.nim-setting-icon i { font-size: 18px; color: #3949ab; }
.nim-setting-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #344767;
  margin-bottom: 2px;
}
.nim-setting-desc {
  font-size: 0.76rem;
  color: #666;
  line-height: 1.45;
}

/* Outcome */
.nim-outcome {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.5;
}
.nim-outcome-icon { font-size: 22px; }

/* Example */
.nim-example {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: #f5f7fa;
  border-left: 4px solid #1976d2;
  font-size: 0.82rem;
  color: #555;
  line-height: 1.6;
}
.nim-example-icon {
  color: #1976d2;
  font-size: 22px;
  flex-shrink: 0;
  margin-top: 2px;
}

/* ── Footer ── */
.nim-footer {
  padding: 0.75rem 1.5rem;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}
.nim-footer-btn {
  background: linear-gradient(135deg, #1976d2, #1565c0);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 24px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: box-shadow 0.15s;
}
.nim-footer-btn:hover {
  box-shadow: 0 4px 14px rgba(25, 118, 210, 0.5);
}
</style>
